import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, map, reduce, switchMap, takeUntil } from 'rxjs/operators';
import { Tpl3plService, Shipment, ShipmentStatsCounts } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { OrdersService } from '../../../services/orders.service';
import { RawResponseModalComponent } from '../shared/raw-response-modal/raw-response-modal.component';
import { OrderDetailDialogComponent } from '../tpl3pl-export-queue/tpl3pl-export-queue.component';

@Component({
	selector: 'app-tpl3pl-shipments',
	templateUrl: './tpl3pl-shipments.component.html',
	styleUrls: ['./tpl3pl-shipments.component.scss']
})
export class Tpl3plShipmentsComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();
	shipments: Shipment[] = [];
	shipmentsTotal = 0;
	pageIndex = 0;
	pageSize = 50;
	readonly pageSizeOptions = [25, 50, 100, 250];
	selectedShipmentIds: number[] = [];
	expandedShipmentId: number | null = null;
	/** Row context for the shared Actions (⋮) menu */
	actionMenuShipment: Shipment | null = null;
	loading = false;
	rawLoading = false;
	statsLoading = false;
	actionInProgressId: number | null = null;
	bulkActionInProgress = false;
	/** Which bulk request is running — used for button spinners (both labels start with "Approving"). */
	bulkActionMode: null | 'approve' | 'invoice' = null;
	/** Shown on the bulk bar and table overlay while a bulk approve / invoice HTTP request is in flight. */
	bulkActionStatusText = '';
	stats: ShipmentStatsCounts | null = null;
	invoiceStatusFilters: string[] = [];
	readonly invoiceStatusOptions: Array<{ value: string; label: string }> = [
		{ value: 'pending', label: 'Pending' },
		{ value: 'approved', label: 'Approved' },
		{ value: 'invoicing', label: 'Invoicing' },
		{ value: 'invoiced', label: 'Invoiced' },
		{ value: 'failed', label: 'Failed' },
		{ value: 'rejected', label: 'Rejected' },
		{ value: 'on_hold', label: 'On hold' },
	];
	ordernoFilter = '';
	erpStatusFilters: number[] = [];
	orderStatuses: any[] = [];
	private static twoWeeksAgo(): Date {
		const d = new Date();
		d.setDate(d.getDate() - 14);
		return d;
	}
	private static ninetyDaysAgo(): Date {
		const d = new Date();
		d.setDate(d.getDate() - 90);
		return d;
	}
	pollDateStart: Date = Tpl3plShipmentsComponent.twoWeeksAgo();
	pollDateEnd: Date = new Date();
	filterDateStart: Date | null = null;
	filterDateEnd: Date | null = null;
	singleOrderToCheck = '';
	checkOrderChannel: 'b2b' | 'b2c' | 'both' = 'both';
	checkOrderLoading = false;
	/** PGS only — check-by-order uses PGS endpoints. */
	showCheckOrderByNo = false;
	lastPollResult: any = null;
	pollStatusMessage = '';
	/** Whether auto_invoice_on_shipment is enabled in config — used to warn before polling. */
	autoInvoiceEnabled = false;

	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private ordersService: OrdersService,
		private route: ActivatedRoute,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.ordersService.getOrderStatuses().subscribe({
			next: (r: any) => {
				this.orderStatuses = Array.isArray(r) ? r : (r && Array.isArray(r.data) ? r.data : []);
			},
			error: () => {
				this.orderStatuses = [];
			}
		});
		this.tpl3plService.getStatus(false).subscribe({
			next: (s) => this.applyTpl3plDashboardStatus(s),
			error: () => {
				this.showCheckOrderByNo = false;
				this.autoInvoiceEnabled = false;
			}
		});
		this.tpl3plService.configSaved$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.tpl3plService.getStatus(true).subscribe({
				next: (s) => this.applyTpl3plDashboardStatus(s),
				error: () => {
					this.showCheckOrderByNo = false;
					this.autoInvoiceEnabled = false;
				}
			});
		});
		this.route.queryParamMap.subscribe(params => {
			const orderFromRoute = params.get('orderno');
			if (orderFromRoute !== null) {
				this.ordernoFilter = orderFromRoute;
			}
			this.applyFilters();
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private applyTpl3plDashboardStatus(s: any): void {
		const configured = !!s?.configured && !!s?.enabled;
		const t = String(s?.integration_type || '').toUpperCase();
		this.showCheckOrderByNo = configured && t === 'PGS';
		this.autoInvoiceEnabled = !!s?.auto_invoice_on_shipment;
	}

	get selectedCount(): number {
		return this.selectedShipmentIds.length;
	}

	/** Rows on this page that can be bulk-approved / bulk-invoiced. */
	get bulkEligibleOnPage(): Shipment[] {
		return this.shipments.filter((s) => this.canSelectShipment(s));
	}

	get canBulkApprove(): boolean {
		return this.selectedCount > 0 && !this.bulkActionInProgress && !this.loading;
	}

	get tableBusy(): boolean {
		return this.loading || this.bulkActionInProgress;
	}

	private clearBulkActionUi(): void {
		this.bulkActionInProgress = false;
		this.bulkActionMode = null;
		this.bulkActionStatusText = '';
	}

	/** Server returns success: true for the endpoint even when some rows fail; use per-result entries. */
	private notifyBulkApproveOutcome(res: any): void {
		const results = Array.isArray(res?.results) ? res.results : [];
		const ok = typeof res?.approved === 'number' ? res.approved : results.filter((r: any) => r?.result?.success).length;
		const processed = typeof res?.processed === 'number' ? res.processed : results.length;
		const failed = processed - ok;
		if (!res?.success) {
			this.globalSearchService.showNotification(res?.message || 'Bulk approve failed', 'warning', 'bottom', 'right');
			return;
		}
		if (failed <= 0) {
			this.globalSearchService.showNotification(`Approved ${ok} shipment(s)`, 'success', 'bottom', 'right');
			return;
		}
		const firstBad = results.find((r: any) => !r?.result?.success);
		const hint = firstBad?.result?.message ? ` — ${firstBad.result.message}` : '';
		this.globalSearchService.showNotification(
			`Approved ${ok} of ${processed} shipment(s). ${failed} failed.${hint}`,
			ok > 0 ? 'warning' : 'danger',
			'bottom',
			'right'
		);
	}

	private notifyBulkInvoiceOutcome(res: any): void {
		const results = Array.isArray(res?.results) ? res.results : [];
		const ok = typeof res?.invoiced === 'number' ? res.invoiced : results.filter((r: any) => r?.result?.success).length;
		const processed = typeof res?.processed === 'number' ? res.processed : results.length;
		const failed = processed - ok;
		if (!res?.success) {
			this.globalSearchService.showNotification(res?.message || 'Bulk invoice failed', 'warning', 'bottom', 'right');
			return;
		}
		if (failed <= 0) {
			this.globalSearchService.showNotification(`Invoiced ${ok} shipment(s)`, 'success', 'bottom', 'right');
			return;
		}
		const firstBad = results.find((r: any) => !r?.result?.success);
		const hint = firstBad?.result?.message ? ` — ${firstBad.result.message}` : '';
		this.globalSearchService.showNotification(
			`Invoiced ${ok} of ${processed} shipment(s). ${failed} failed.${hint}`,
			ok > 0 ? 'warning' : 'danger',
			'bottom',
			'right'
		);
	}

	get orderStatusesForSelect(): any[] {
		return [...this.orderStatuses]
			.filter((s) => s && !isNaN(Number(s.order_status_id)))
			.sort((a, b) => Number(a.order_status_id) - Number(b.order_status_id));
	}

	loadShipments(): void {
		this.loading = true;
		const filters: any = {
			limit: this.pageSize,
			offset: this.pageIndex * this.pageSize
		};
		if (this.filterDateStart) {
			filters.date_from = this.formatDate(this.filterDateStart);
		}
		if (this.filterDateEnd) {
			filters.date_to = this.formatDate(this.filterDateEnd);
		}
		if (this.ordernoFilter) {
			filters.orderno = this.ordernoFilter;
		}
		if (this.invoiceStatusFilters.length > 0) {
			filters.invoice_statuses = [...this.invoiceStatusFilters];
		}
		if (this.erpStatusFilters.length > 0) {
			filters.erp_status_ids = [...this.erpStatusFilters];
		}

		this.tpl3plService.getShipments(filters).subscribe({
			next: (result) => {
				this.shipments = result.data;
				this.shipmentsTotal = result.total ?? 0;
				const pageIds = new Set(this.shipments.map((s) => s.id));
				this.selectedShipmentIds = this.selectedShipmentIds.filter((id) => {
					if (!pageIds.has(id)) {
						return true;
					}
					const row = this.shipments.find((s) => s.id === id);
					return !!row && this.canSelectShipment(row);
				});
				this.loading = false;
			},
			error: () => {
				this.globalSearchService.showNotification('Error loading shipments', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	loadStats(): void {
		this.statsLoading = true;
		this.tpl3plService.getShipmentStats().subscribe({
			next: (res) => {
				this.statsLoading = false;
				if (res.success) {
					this.stats = res.counts;
				}
			},
			error: () => {
				this.statsLoading = false;
			}
		});
	}

	applyFilters(): void {
		this.pageIndex = 0;
		this.loadShipments();
		this.loadStats();
	}

	clearFilters(): void {
		this.ordernoFilter = '';
		this.invoiceStatusFilters = [];
		this.erpStatusFilters = [];
		this.filterDateStart = null;
		this.filterDateEnd = null;
		this.pollDateStart = Tpl3plShipmentsComponent.twoWeeksAgo();
		this.pollDateEnd = new Date();
		this.applyFilters();
	}

	setQuickDateFilter(days: number): void {
		const end = new Date();
		const start = new Date();
		start.setDate(start.getDate() - days);
		this.filterDateStart = start;
		this.filterDateEnd = end;
		this.applyFilters();
	}

	private formatDate(d: any): string {
		const date = d instanceof Date ? d : new Date(d);
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	private formatDateRange(): { from: string; to: string } {
		return {
			from: this.formatDate(this.pollDateStart),
			to: this.formatDate(this.pollDateEnd)
		};
	}

	/** Swap Poll from / Poll to when reversed so ranges and raw preview work. */
	private normalizePollDateRange(): void {
		if (this.pollDateStart && this.pollDateEnd && this.pollDateStart > this.pollDateEnd) {
			const t = this.pollDateStart;
			this.pollDateStart = this.pollDateEnd;
			this.pollDateEnd = t;
		}
	}

	private getDatesInRange(from: string, to: string): string[] {
		const dates: string[] = [];
		const start = new Date(from);
		const end = new Date(to);
		if (start > end) {
			return [];
		}
		const cursor = new Date(start);
		while (cursor <= end) {
			dates.push(this.formatDate(cursor));
			cursor.setDate(cursor.getDate() + 1);
		}
		return dates;
	}

	pollShipments(): void {
		this.normalizePollDateRange();
		const { from, to } = this.formatDateRange();
		const dates = this.getDatesInRange(from, to);
		if (dates.length === 0) {
			this.globalSearchService.showNotification('Invalid date range: from must be on or before to', 'warning', 'bottom', 'right');
			return;
		}
		if (dates.length > 60) {
			this.globalSearchService.showNotification('Date range too large. Maximum 60 days at once.', 'warning', 'bottom', 'right');
			return;
		}
		if (this.autoInvoiceEnabled) {
			const daysLabel = dates.length === 1 ? `1 day (${from})` : `${dates.length} days (${from} – ${to})`;
			const confirmed = window.confirm(
				`Auto-invoice on shipment is ENABLED in configuration.\n\n`
				+ `Polling ${daysLabel} will automatically invoice any newly imported shipments that have line items.\n\n`
				+ `Continue?`
			);
			if (!confirmed) {
				return;
			}
		}
		this.loading = true;
		this.lastPollResult = null;
		this.pollStatusMessage = dates.length === 1 ? `Polling ${dates[0]}…` : `Polling ${dates.length} days (${from} – ${to})…`;

		const pollOne = (date: string): Observable<any> =>
			this.tpl3plService.pollShipments(date, date);

		const startTime = Date.now();
		of(...dates).pipe(
			concatMap((date, index) => {
				const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
				this.pollStatusMessage = dates.length === 1
					? `Polling ${date}… (${elapsed}s)`
					: `Polling day ${index + 1} of ${dates.length}: ${date}… (${elapsed}s)`;
				return pollOne(date);
			}),
			reduce(
				(acc: { processed: number; success: boolean; message: string; response: any }, result) => {
					const ok = result && (result.success === true || (result.success !== false && result.response_b2b?.code === 200));
					const processed = result?.processed ?? (Array.isArray(result?.data) ? result.data.length : 0);
					acc.processed += processed;
					if (!ok) {
						acc.success = false;
						acc.message = result?.message ?? 'One or more days failed';
						acc.response = result?.response ?? result?.response_b2b ?? acc.response;
					}
					return acc;
				},
				{ processed: 0, success: true, message: '', response: null }
			)
		).subscribe({
			next: (agg) => {
				this.pollStatusMessage = '';
				this.lastPollResult = {
					success: agg.success,
					processed: agg.processed,
					message: agg.success
						? (dates.length === 1 ? (agg.processed ? `${agg.processed} shipment(s) processed` : 'No shipments for this date')
							: `Polled ${dates.length} days, ${agg.processed} shipment(s) processed`)
						: agg.message,
					response: agg.response
				};
				if (agg.success) {
					this.globalSearchService.showNotification(
						`Processed ${agg.processed} shipments from ${dates.length} day(s)`,
						'success', 'bottom', 'right'
					);
					this.pageIndex = 0;
					this.applyFilters();
				} else {
					this.globalSearchService.showNotification('Poll failed: ' + agg.message, 'danger', 'bottom', 'right');
				}
				this.loading = false;
			},
			error: (error) => {
				this.pollStatusMessage = '';
				this.lastPollResult = {
					success: false,
					message: error?.error?.message || error?.message || 'Error polling shipments',
					response: error?.error?.response ?? error?.error
				};
				this.globalSearchService.showNotification('Error polling shipments', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	onPage(event: { pageIndex: number; pageSize: number }): void {
		this.pageIndex = event.pageIndex;
		this.pageSize = event.pageSize;
		this.loadShipments();
	}

	toggleSelect(shipment: Shipment, checked: boolean): void {
		if (!shipment || !shipment.id || !this.canSelectShipment(shipment)) {
			return;
		}
		if (checked) {
			if (!this.selectedShipmentIds.includes(shipment.id)) {
				this.selectedShipmentIds = [...this.selectedShipmentIds, shipment.id];
			}
			return;
		}
		this.selectedShipmentIds = this.selectedShipmentIds.filter((id) => id !== shipment.id);
	}

	toggleSelectAllPending(checked: boolean): void {
		const eligibleIds = this.bulkEligibleOnPage.map((s) => s.id);
		if (!checked) {
			this.selectedShipmentIds = this.selectedShipmentIds.filter((id) => !eligibleIds.includes(id));
			return;
		}
		this.selectedShipmentIds = Array.from(new Set<number>([...this.selectedShipmentIds, ...eligibleIds]).values());
	}

	isSelected(shipment: Shipment): boolean {
		return !!shipment?.id && this.selectedShipmentIds.includes(shipment.id);
	}

	isAllPendingSelected(): boolean {
		const eligible = this.bulkEligibleOnPage;
		if (eligible.length === 0) {
			return false;
		}
		return eligible.every((s) => this.selectedShipmentIds.includes(s.id));
	}

	/** Header checkbox: some but not all eligible rows on this page are selected. */
	isBulkSelectionIndeterminate(): boolean {
		const eligible = this.bulkEligibleOnPage;
		if (eligible.length === 0) {
			return false;
		}
		const n = eligible.filter((s) => this.selectedShipmentIds.includes(s.id)).length;
		return n > 0 && n < eligible.length;
	}

	canSelectShipment(shipment: Shipment): boolean {
		const status = this.resolveInvoiceStatus(shipment);
		return status === 'pending' || status === 'approved' || status === 'failed';
	}

	viewOrder(orderno: string): void {
		if (!orderno) {
			return;
		}
		this.dialog.open(OrderDetailDialogComponent, {
			data: { orderno },
			width: '90vw',
			maxWidth: '1100px',
			maxHeight: '90vh',
			panelClass: 'order-detail-panel',
		});
	}

	// ── Actions menu (shared mat-menu) ─────────────────────────────────────────
	runMenuPreview(): void {
		const s = this.actionMenuShipment;
		if (s) {
			this.openInvoicePreview(s);
		}
	}

	runMenuMarkInvoiced(): void {
		const s = this.actionMenuShipment;
		if (s) {
			this.markInvoiced(s);
		}
	}

	runMenuMarkCaptured(): void {
		const s = this.actionMenuShipment;
		if (s) {
			this.markCaptured(s);
		}
	}

	runMenuApprove(): void {
		const s = this.actionMenuShipment;
		if (s) {
			this.approveShipment(s);
		}
	}

	runMenuApproveAndInvoice(): void {
		const s = this.actionMenuShipment;
		if (s) {
			this.approveAndInvoiceShipment(s);
		}
	}

	runMenuRetryInvoice(): void {
		const s = this.actionMenuShipment;
		if (s) {
			this.retryInvoiceShipment(s);
		}
	}

	runMenuHold(): void {
		const s = this.actionMenuShipment;
		if (s) {
			this.holdShipment(s);
		}
	}

	runMenuReleaseHold(): void {
		const s = this.actionMenuShipment;
		if (s) {
			this.releaseShipmentHold(s);
		}
	}

	runMenuReject(): void {
		const s = this.actionMenuShipment;
		if (s) {
			this.rejectShipment(s);
		}
	}

	menuCanMarkInvoiced(): boolean {
		const s = this.actionMenuShipment;
		if (!s) {
			return false;
		}
		const st = this.resolveInvoiceStatus(s);
		return st !== 'invoiced' && st !== 'rejected';
	}

	menuCanMarkCaptured(): boolean {
		const s = this.actionMenuShipment;
		return !!(s && (s.reconcile_auth_id || s.reconcile_transid));
	}

	menuCanApprove(): boolean {
		const s = this.actionMenuShipment;
		return !!s && this.resolveInvoiceStatus(s) === 'pending';
	}

	menuCanApproveAndInvoice(): boolean {
		const s = this.actionMenuShipment;
		if (!s) {
			return false;
		}
		const st = this.resolveInvoiceStatus(s);
		return st === 'pending' || st === 'approved';
	}

	menuCanRetryInvoice(): boolean {
		const s = this.actionMenuShipment;
		return !!s && this.resolveInvoiceStatus(s) === 'failed';
	}

	menuCanHold(): boolean {
		const s = this.actionMenuShipment;
		if (!s) {
			return false;
		}
		const st = this.resolveInvoiceStatus(s);
		return st !== 'on_hold' && st !== 'invoiced' && st !== 'rejected';
	}

	menuCanReleaseHold(): boolean {
		const s = this.actionMenuShipment;
		return !!s && this.resolveInvoiceStatus(s) === 'on_hold';
	}

	menuCanReject(): boolean {
		const s = this.actionMenuShipment;
		if (!s) {
			return false;
		}
		const st = this.resolveInvoiceStatus(s);
		return st !== 'invoiced' && st !== 'rejected';
	}

	openInvoicePreview(shipment: Shipment): void {
		if (!shipment?.id) {
			return;
		}
		const ref = this.dialog.open(ShipmentInvoicePreviewDialogComponent, {
			data: { shipment, tpl3plService: this.tpl3plService },
			width: '96vw',
			maxWidth: '1320px',
			maxHeight: '92vh',
			panelClass: 'shipment-preview-panel',
		});
		ref.afterClosed().subscribe((action: string | undefined) => {
			if (action === 'approve') {
				this.approveShipment(shipment);
			} else if (action === 'approve_and_invoice') {
				this.approveAndInvoiceShipment(shipment);
			} else if (action === 'retry') {
				this.retryInvoiceShipment(shipment);
			} else if (action === 'mark_captured') {
				this.markCaptured(shipment);
			} else if (action === 'capture_applied') {
				this.loadShipments();
				this.loadStats();
			}
		});
	}

	private runShipmentAction(shipment: Shipment, action: Observable<any>, successMessage: string): void {
		if (!shipment?.id) {
			return;
		}
		this.actionInProgressId = shipment.id;
		action.subscribe({
			next: (res) => {
				this.actionInProgressId = null;
				if (res?.success) {
					this.globalSearchService.showNotification(successMessage, 'success', 'bottom', 'right');
				} else {
					this.globalSearchService.showNotification(res?.message || 'Action failed', 'warning', 'bottom', 'right');
				}
				this.loadShipments();
				this.loadStats();
			},
			error: (err) => {
				this.actionInProgressId = null;
				this.globalSearchService.showNotification(err?.error?.message || 'Action failed', 'danger', 'bottom', 'right');
				this.loadShipments();
				this.loadStats();
			}
		});
	}

	approveShipment(shipment: Shipment): void {
		this.runShipmentAction(shipment, this.tpl3plService.approveShipment(shipment.id, false), 'Shipment approved');
	}

	approveAndInvoiceShipment(shipment: Shipment): void {
		this.runShipmentAction(shipment, this.tpl3plService.approveAndInvoiceShipment(shipment.id), 'Shipment invoiced');
	}

	retryInvoiceShipment(shipment: Shipment): void {
		this.runShipmentAction(shipment, this.tpl3plService.retryShipmentInvoice(shipment.id), 'Invoice retry started');
	}

	/** When debtortrans already has a sales invoice but tpl3pl_shipments.invoice_status is still pending/failed/etc. */
	markInvoiced(shipment: Shipment): void {
		this.submitMarkInvoiced(shipment, false);
	}

	private submitMarkInvoiced(shipment: Shipment, force: boolean): void {
		if (!shipment?.id) {
			return;
		}
		this.actionInProgressId = shipment.id;
		this.tpl3plService.markShipmentInvoiced(shipment.id, force).subscribe({
			next: (res) => {
				this.actionInProgressId = null;
				if (res?.success) {
					this.globalSearchService.showNotification(res.message || 'Shipment marked invoiced', 'success', 'bottom', 'right');
					this.loadShipments();
					this.loadStats();
					return;
				}
				if (res?.needs_force && !force) {
					const msg = (res.message || 'No sales invoice row found for this order.') + '\n\nMark this shipment as invoiced anyway?';
					if (window.confirm(msg)) {
						this.submitMarkInvoiced(shipment, true);
					}
					return;
				}
				this.globalSearchService.showNotification(res?.message || 'Could not mark invoiced', 'warning', 'bottom', 'right');
			},
			error: (err) => {
				this.actionInProgressId = null;
				this.globalSearchService.showNotification(err?.error?.message || 'Action failed', 'danger', 'bottom', 'right');
				this.loadShipments();
				this.loadStats();
			}
		});
	}

	holdShipment(shipment: Shipment): void {
		this.runShipmentAction(shipment, this.tpl3plService.holdShipment(shipment.id), 'Shipment placed on hold');
	}

	releaseShipmentHold(shipment: Shipment): void {
		this.runShipmentAction(shipment, this.tpl3plService.releaseShipmentHold(shipment.id), 'Shipment released');
	}

	rejectShipment(shipment: Shipment): void {
		const reason = window.prompt('Reason for rejection', shipment.rejected_reason || '');
		if (reason === null) {
			return;
		}
		const trimmed = reason.trim();
		if (trimmed === '') {
			this.globalSearchService.showNotification('Rejection reason is required', 'warning', 'bottom', 'right');
			return;
		}
		this.runShipmentAction(shipment, this.tpl3plService.rejectShipment(shipment.id, trimmed), 'Shipment rejected');
	}

	markCaptured(shipment: Shipment): void {
		const authId = shipment.reconcile_auth_id;
		const transid = shipment.reconcile_transid;
		if (!authId && !transid) {
			this.globalSearchService.showNotification(
				'ERP Gap is not set for this order (only when captured auth has neither capture nor cc_transactions row; either record clears the gap)',
				'warning',
				'bottom',
				'right'
			);
			return;
		}
		const defaultAmount =
			shipment.invoice_total ??
			shipment.reconcile_auth_amount ??
			shipment.last_auth_amount ??
			shipment.active_auth_total ??
			0;
		const input = window.prompt(
			`Record missing ERP capture / card transaction for order ${shipment.orderno}\n`
			+ `(Does not call Stripe — use when the gateway already captured but ERP rows are missing.)\n\n`
			+ `Invoice: $${(shipment.invoice_total ?? 0).toFixed(2)}  Auth on file: $${(shipment.reconcile_auth_amount ?? shipment.last_auth_amount ?? 0).toFixed(2)}\n\n`
			+ `Amount to record:`,
			defaultAmount.toFixed(2)
		);
		if (input === null) { return; }
		const amount = parseFloat(input);
		if (isNaN(amount) || amount <= 0) {
			this.globalSearchService.showNotification('Invalid amount', 'warning', 'bottom', 'right');
			return;
		}
		this.actionInProgressId = shipment.id;
		this.tpl3plService.markCaptured({
			transid: transid || '',
			auth_id: authId || '',
			orderno: shipment.orderno,
			amount,
			note: 'Recorded from 3PL shipments (missing ERP capture rows)'
		}).subscribe({
			next: (res) => {
				this.actionInProgressId = null;
				if (res.success) {
					this.globalSearchService.showNotification(`Marked captured $${amount.toFixed(2)} for order ${shipment.orderno}`, 'success', 'bottom', 'right');
					this.loadShipments();
				} else {
					this.globalSearchService.showNotification(res.message || 'Mark captured failed', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.actionInProgressId = null;
				this.globalSearchService.showNotification(err?.error?.message || 'Mark captured failed', 'danger', 'bottom', 'right');
			}
		});
	}

	bulkApprove(): void {
		if (!this.canBulkApprove) {
			return;
		}
		const n = this.selectedShipmentIds.length;
		this.bulkActionInProgress = true;
		this.bulkActionMode = 'approve';
		this.bulkActionStatusText = `Approving ${n} shipment(s)…`;
		this.tpl3plService.bulkApproveShipments([...this.selectedShipmentIds]).subscribe({
			next: (res) => {
				this.clearBulkActionUi();
				this.selectedShipmentIds = [];
				this.notifyBulkApproveOutcome(res);
				this.loadShipments();
				this.loadStats();
			},
			error: (err) => {
				this.clearBulkActionUi();
				this.globalSearchService.showNotification(err?.error?.message || 'Bulk approve failed', 'danger', 'bottom', 'right');
			}
		});
	}

	bulkApproveAndInvoice(): void {
		if (!this.canBulkApprove) {
			return;
		}
		const n = this.selectedShipmentIds.length;
		this.bulkActionInProgress = true;
		this.bulkActionMode = 'invoice';
		this.bulkActionStatusText = `Approving & invoicing ${n} shipment(s)… This may take a while.`;
		this.tpl3plService.bulkInvoiceShipments([...this.selectedShipmentIds]).subscribe({
			next: (res) => {
				this.clearBulkActionUi();
				this.selectedShipmentIds = [];
				this.notifyBulkInvoiceOutcome(res);
				this.loadShipments();
				this.loadStats();
			},
			error: (err) => {
				this.clearBulkActionUi();
				this.globalSearchService.showNotification(err?.error?.message || 'Bulk invoice failed', 'danger', 'bottom', 'right');
			}
		});
	}

	toggleExpand(shipment: Shipment): void {
		this.expandedShipmentId = this.expandedShipmentId === shipment.id ? null : shipment.id;
	}

	/** Normalize phase strings from API / DB (casing, hyphens, rare camelCase). */
	private normalizePhase(phase: string | undefined | null): string {
		return (phase ?? '')
			.toString()
			.trim()
			.toLowerCase()
			.replace(/-/g, '_');
	}

	/** For templates: normalized processed_phase / processedPhase. */
	processedPhaseNorm(item: Shipment): string {
		return this.normalizePhase(
			(item as Shipment & { processedPhase?: string }).processed_phase ??
				(item as Shipment & { processedPhase?: string }).processedPhase
		);
	}

	phaseLabel(phase: string | undefined): string {
		const p = this.normalizePhase(phase);
		if (!p) {
			return 'Pre-invoice';
		}
		const labels: Record<string, string> = {
			pre_invoice: 'Pre-invoice',
			pending_review: 'Pending review',
			auto_invoiced: 'Auto-invoiced',
			invoice_failed: 'Invoice failed'
		};
		return labels[p] || (phase ?? '');
	}

	phaseLabelFromItem(item: Shipment): string {
		const ext = item as Shipment & { processedPhase?: string };
		return this.phaseLabel(ext.processed_phase ?? ext.processedPhase);
	}

	/** True when list join found a sales invoice in ERP (debtortrans). */
	private hasErpInvoiceRow(item: Shipment): boolean {
		if (item.invoice_transno != null && String(item.invoice_transno).trim() !== '') {
			return true;
		}
		return item.invoice_total != null && Number(item.invoice_total) > 0;
	}

	/**
	 * Effective queue status for display and actions. Must not contradict processed_phase:
	 * the DB can have invoice_status = 'pending' while processed_phase = 'auto_invoiced'
	 * (reconcile never wrote invoice_status). Phase wins for success/failure.
	 * Also: if ERP already has an invoice row but tpl3pl_shipments was never updated, treat as invoiced.
	 */
	resolveInvoiceStatus(item: Shipment): string {
		const raw = (item.invoice_status || '').trim();
		const rawLower = raw.toLowerCase();
		if (rawLower === 'rejected' || rawLower === 'on_hold') {
			return rawLower === 'on_hold' ? 'on_hold' : 'rejected';
		}

		const phase = this.processedPhaseNorm(item);
		if (phase === 'auto_invoiced') {
			return 'invoiced';
		}
		if (phase === 'invoice_failed') {
			return 'failed';
		}
		if (rawLower === 'invoiced') {
			return 'invoiced';
		}
		if (this.hasErpInvoiceRow(item)) {
			return 'invoiced';
		}
		if (rawLower === 'invoicing' || rawLower === 'approved' || rawLower === 'failed') {
			return rawLower;
		}
		return rawLower || 'pending';
	}

	invoiceStatusLabel(status: string): string {
		const labels: Record<string, string> = {
			pending: 'Pending',
			approved: 'Approved',
			invoicing: 'Invoicing',
			invoiced: 'Invoiced',
			failed: 'Failed',
			rejected: 'Rejected',
			on_hold: 'On hold'
		};
		return labels[status] || status;
	}

	invoiceStatusClass(status: string): string {
		switch (status) {
		case 'pending':
			return 'status-pending';
		case 'approved':
			return 'status-approved';
		case 'invoicing':
			return 'status-invoicing';
		case 'invoiced':
			return 'status-invoiced';
		case 'failed':
			return 'status-failed';
		case 'rejected':
			return 'status-rejected';
		case 'on_hold':
			return 'status-on-hold';
		default:
			return 'status-default';
		}
	}

	/** Short label for authorizations.status on the order (latest auth row). */
	lastAuthStatusLabel(status: string | null | undefined): string {
		const s = (status || '').toLowerCase().trim();
		if (s === 'c') { return 'C'; }
		if (s === '1' || s === 'approved') { return 'Open'; }
		if (s === 'requires_capture') { return 'Cap?'; }
		if (s === 'voided') { return 'Void'; }
		return s ? s.slice(0, 6) : '—';
	}

	checkSingleOrder(): void {
		const orderNo = (this.singleOrderToCheck || '').trim();
		if (!orderNo) {
			this.globalSearchService.showNotification('Enter an order number to check', 'warning', 'bottom', 'right');
			return;
		}
		this.checkOrderLoading = true;
		this.lastPollResult = null;
		this.tpl3plService.pollShipmentsByOrderNo(orderNo, this.checkOrderChannel).subscribe({
			next: (result) => {
				this.lastPollResult = result;
				this.checkOrderLoading = false;
				if (result.success) {
					const channelLabel = (result.channel === 'b2b' || result.channel === 'b2c') ? ` (${result.channel.toUpperCase()})` : '';
					this.globalSearchService.showNotification(
						result.processed ? `Processed ${result.processed} shipment(s) for order ${orderNo}${channelLabel}` : (result.message || 'Done'),
						'success', 'bottom', 'right'
					);
					this.ordernoFilter = orderNo;
					this.pageIndex = 0;
					this.applyFilters();
				} else {
					this.globalSearchService.showNotification('Check failed: ' + (result.message || 'Unknown error'), 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.checkOrderLoading = false;
				this.lastPollResult = { success: false, message: err?.error?.message || err?.message || 'Error checking order' };
				this.globalSearchService.showNotification('Error checking order', 'danger', 'bottom', 'right');
			}
		});
	}

	openRawRequestResponse(): void {
		this.normalizePollDateRange();
		const { from, to } = this.formatDateRange();
		const dates = this.getDatesInRange(from, to);
		if (dates.length === 0) {
			this.globalSearchService.showNotification('Invalid date range', 'warning', 'bottom', 'right');
			return;
		}
		this.rawLoading = true;

		const openModal = (
			byDate: { date: string; request_payload: any; response_b2b: any; response_b2c: any; success: boolean }[]
		): void => {
			this.rawLoading = false;
			const apiCalls = byDate.flatMap((d) => {
				const arr = Array.isArray(d.request_payload) ? d.request_payload : (d.request_payload != null ? [d.request_payload] : []);
				return arr.map((req: any) =>
					req && typeof req === 'object' ? { date: d.date, ...req } : { date: d.date, request: req }
				);
			});
			const request_payload = {
				date_range: { from: dates[0], to: dates[dates.length - 1], days: dates.length },
				api_calls: apiCalls
			};
			const response = byDate.length === 1
				? { b2b: byDate[0].response_b2b, b2c: byDate[0].response_b2c }
				: { byDate: byDate.map((d) => ({ date: d.date, b2b: d.response_b2b, b2c: d.response_b2c, success: d.success })) };
			const content: any = { request_payload, response };
			this.dialog.open(RawResponseModalComponent, {
				width: '720px',
				maxWidth: '96vw',
				maxHeight: '90vh',
				data: {
					context: 'shipments',
					content,
					onSent: () => this.applyFilters()
				},
				panelClass: 'raw-response-dialog'
			});
		};

		const pollOneRaw = (date: string) =>
			this.tpl3plService.pollShipmentsRaw(date, date).pipe(
				map((res) => ({ date, res })),
				catchError((err) =>
					of({
						date,
						res: {
							success: false,
							message: err?.error?.message || err?.message || 'Request failed',
							request_payload: null,
							response_b2b: err?.error ?? null,
							response_b2c: null
						}
					})
				)
			);

		this.tpl3plService.getStatus(true).pipe(
			switchMap((status) => {
				const type = String(status?.integration_type || '').toUpperCase();
				if (type === 'API') {
					return this.tpl3plService.pollShipmentsRaw(from, to).pipe(
						map((res) => [
							{
								date: 'API',
								request_payload: res.request_payload ?? null,
								response_b2b: res.response_b2b ?? null,
								response_b2c: res.response_b2c ?? null,
								success: !!res.success
							}
						])
					);
				}
				if (type !== '' && type !== 'PGS') {
					return this.tpl3plService.pollShipmentsRaw(dates[0], dates[0]).pipe(
						map((res) => [
							{
								date: type,
								request_payload: res.request_payload ?? null,
								response_b2b: res.response_b2b ?? null,
								response_b2c: res.response_b2c ?? null,
								success: !!res.success
							}
						])
					);
				}
				return of(...dates).pipe(
					concatMap((date) => pollOneRaw(date)),
					reduce(
						(acc: { date: string; request_payload: any; response_b2b: any; response_b2c: any; success: boolean }[], { date, res }: { date: string; res: any }) => {
							acc.push({
								date,
								request_payload: res.request_payload ?? null,
								response_b2b: res.response_b2b ?? null,
								response_b2c: res.response_b2c ?? null,
								success: !!res.success
							});
							return acc;
						},
						[] as { date: string; request_payload: any; response_b2b: any; response_b2c: any; success: boolean }[]
					)
				);
			})
		).subscribe({
			next: (byDate) => openModal(byDate),
			error: (err) => {
				this.rawLoading = false;
				const msg = err?.error?.message || err?.message || 'Could not load raw request/response';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			}
		});
	}
}

@Component({
	selector: 'app-shipment-invoice-preview-dialog',
	template: `
<div class="sipd-wrap">
	<div class="sipd-header">
		<div class="sipd-title">
			<mat-icon class="sipd-title-icon">receipt_long</mat-icon>
			<span>Invoice Preview</span>
			<span class="sipd-order-no" *ngIf="preview">— {{ preview.shipment.orderno }}</span>
		</div>
		<button mat-icon-button (click)="close()" class="sipd-close-btn" matTooltip="Close"><mat-icon>close</mat-icon></button>
	</div>

	<div class="sipd-loading" *ngIf="loading">
		<mat-spinner diameter="48"></mat-spinner>
		<p>Loading preview…</p>
	</div>

	<div class="sipd-error" *ngIf="!loading && error">
		<mat-icon color="warn">error_outline</mat-icon>
		<span>{{ error }}</span>
	</div>

	<ng-container *ngIf="!loading && preview">
		<mat-tab-group class="sipd-tab-group" animationDuration="0ms">
			<mat-tab label="Invoice">
				<ng-template matTabContent>
					<div class="sipd-tab-scroll">
						<div class="sipd-meta-row">
							<div class="sipd-meta-block">
								<span class="sipd-meta-label">Customer</span>
								<span class="sipd-meta-value">{{ preview.order.customer_name || preview.order.debtorno }}</span>
							</div>
							<div class="sipd-meta-block">
								<span class="sipd-meta-label">Carrier</span>
								<span class="sipd-meta-value">{{ preview.shipment.carrier || '—' }}</span>
							</div>
							<div class="sipd-meta-block">
								<span class="sipd-meta-label">Tracking</span>
								<span class="sipd-meta-value">{{ preview.shipment.tracking_number || '—' }}</span>
							</div>
							<div class="sipd-meta-block">
								<span class="sipd-meta-label">Ship Date</span>
								<span class="sipd-meta-value">{{ preview.shipment.ship_date | date:'mediumDate' }}</span>
							</div>
							<div class="sipd-meta-block">
								<span class="sipd-meta-label">Pay Terms</span>
								<span class="sipd-meta-value">{{ preview.order.payterms || '—' }}</span>
							</div>
							<div class="sipd-meta-block">
								<span class="sipd-meta-label">Status</span>
								<span class="sipd-meta-value sipd-status-badge" [ngClass]="'status-' + displayStatus">{{ displayStatusLabel }}</span>
							</div>
						</div>

						<div class="sipd-section-title">Line Items</div>
						<div class="sipd-lines-wrap">
							<table class="sipd-lines-table">
								<thead>
									<tr>
										<th>SKU</th>
										<th class="text-right">Ordered</th>
										<th class="text-right">Invoiced</th>
										<th class="text-right">Remaining</th>
										<th class="text-right">Shipped</th>
										<th class="text-right">Will Invoice</th>
										<th class="text-right">Unit Price</th>
										<th class="text-right">Disc%</th>
										<th class="text-right">Invoiced $</th>
										<th class="text-right">Line Total</th>
									</tr>
								</thead>
								<tbody>
									<tr *ngFor="let line of preview.lines" [ngClass]="{'sipd-line-mismatch': line.mismatch, 'sipd-line-zero': line.will_invoice === 0}">
										<td class="sipd-sku">{{ line.sku }}</td>
										<td class="text-right">{{ line.ordered }}</td>
										<td class="text-right">{{ line.already_invoiced }}</td>
										<td class="text-right">{{ line.remaining }}</td>
										<td class="text-right" [ngClass]="{'sipd-qty-mismatch': line.mismatch}">
											{{ line.shipment_qty > 0 ? line.shipment_qty : '—' }}
											<mat-icon class="sipd-mismatch-icon" *ngIf="line.mismatch" matTooltip="Shipment qty differs from invoiceable qty">warning</mat-icon>
										</td>
										<td class="text-right"><strong>{{ line.will_invoice }}</strong></td>
										<td class="text-right">{{ line.unit_price | currency }}</td>
										<td class="text-right">{{ line.discount_pct > 0 ? (line.discount_pct * 100 | number:'1.0-2') + '%' : '—' }}</td>
										<td class="text-right">{{ line.already_invoiced > 0 ? (line.invoiced_line_total | currency) : '—' }}</td>
										<td class="text-right"><strong>{{ (line.line_display_total != null ? line.line_display_total : line.line_total) | currency }}</strong></td>
									</tr>
									<tr *ngIf="!preview.lines || preview.lines.length === 0">
										<td colspan="10" class="text-center text-muted">No lines</td>
									</tr>
								</tbody>
							</table>
						</div>

						<div class="sipd-totals-note" *ngIf="preview.totals_note">{{ preview.totals_note }}</div>

						<div class="sipd-totals-row">
							<!-- Payment / auth summary (left) -->
							<div class="sipd-totals-auth" *ngIf="preview.preauth?.enabled">
								<div class="sipd-totals-auth-title">
									<mat-icon class="sipd-ta-icon" [ngClass]="preauthBannerClass()">{{ preauthBannerIcon() }}</mat-icon>
									{{ preauthTitle() }}
								</div>
								<ng-container *ngIf="displayStatus !== 'invoiced'">
									<div class="sipd-ta-line">
										<span>Active hold</span><span>{{ preview.preauth.active_auth_total | currency }}</span>
									</div>
									<div class="sipd-ta-line">
										<span>This invoice</span><span>{{ preview.preauth.invoice_total | currency }}</span>
									</div>
									<div class="sipd-ta-line" *ngIf="preview.preauth.mismatch">
										<span class="sipd-ta-warn">Δ mismatch</span>
										<span class="sipd-ta-warn">{{ (preview.preauth.invoice_total - preview.preauth.active_auth_total) | currency }}</span>
									</div>
									<div class="sipd-ta-line" *ngIf="preview.preauth.new_auth_required">
										<span class="sipd-ta-info">New auth required</span>
									</div>
								</ng-container>
								<ng-container *ngIf="displayStatus === 'invoiced'">
									<div class="sipd-ta-line" *ngIf="preview.preauth.captured_auth_total > 0">
										<span>Captured</span><span>{{ preview.preauth.captured_auth_total | currency }}</span>
									</div>
								</ng-container>
								<div class="sipd-ta-auths" *ngIf="preview.preauth.all_auths?.length">
									<div class="sipd-auth-cards sipd-auth-cards--inline">
										<div *ngFor="let a of preview.preauth.all_auths" class="sipd-auth-card" [ngClass]="authRowClass(a)">
											<div class="sipd-auth-card-row1">
												<span class="sipd-auth-status-badge" [ngClass]="authStatusClass(a)">{{ a.status_label }}</span>
												<span class="sipd-auth-card-amt">{{ a.amount | currency }}</span>
											</div>
											<div class="sipd-auth-card-row2">{{ a.transdate | date:'mediumDate' }}</div>
											<div class="sipd-auth-card-ref" *ngIf="a.transid" [matTooltip]="a.transid" matTooltipShowDelay="300"><span class="sipd-auth-ref-lbl">Ref</span> {{ authShortRef(a) }}</div>
										</div>
									</div>
								</div>
								<div class="sipd-no-auths sipd-no-auths--inline" *ngIf="!preauthHasAuthRecords && (preview.shipment_slice_total ?? 0) > 0.004">
									<mat-icon>info_outline</mat-icon> No auth records found.
								</div>
							</div>
							<div class="sipd-totals-auth sipd-totals-auth--none" *ngIf="!preview.preauth?.enabled">
								<mat-icon>lock_open</mat-icon>
								<span class="sipd-ta-noauth">No pre-auth applies</span>
							</div>

							<!-- Totals (right) -->
							<div class="sipd-totals-block">
								<div class="sipd-totals-line" *ngIf="preview.totals.freight > 0">
									<span>Freight</span><span>{{ preview.totals.freight | currency }}</span>
								</div>
								<div class="sipd-totals-line" *ngFor="let ta of preview.totals.tax_authorities">
									<span>{{ ta.description }} ({{ ta.taxrate * 100 | number:'1.0-2' }}%)</span><span>{{ ta.total | currency }}</span>
								</div>
								<div class="sipd-totals-line sipd-totals-subtotal">
									<span>Subtotal</span><span>{{ preview.totals.subtotal | currency }}</span>
								</div>
								<div class="sipd-totals-line sipd-totals-tax" *ngIf="preview.totals.tax > 0">
									<span>Total Tax</span><span>{{ preview.totals.tax | currency }}</span>
								</div>
								<div class="sipd-totals-line sipd-totals-total">
									<span>Invoice Total</span><span>{{ preview.totals.total | currency }}</span>
								</div>
							</div>
						</div>

						<div class="sipd-prev-error" *ngIf="preview.shipment.invoice_message">
							<mat-icon color="warn">error</mat-icon>
							<span><strong>Previous error:</strong> {{ preview.shipment.invoice_message }}</span>
						</div>
					</div>
				</ng-template>
			</mat-tab>

			<mat-tab label="Customer email">
				<ng-template matTabContent>
					<div class="sipd-tab-scroll sipd-tab-email">
						<div class="sipd-email-warn" *ngIf="emailPreview && !emailPreview.email_enabled_in_config">
							<mat-icon>mail_outline</mat-icon>
							<span>“Email customer on 3PL invoice” is <strong>disabled</strong> in 3PL configuration — no automatic send.</span>
						</div>
						<div class="sipd-email-sent" *ngIf="emailPreview?.customer_invoice_email_sent_at">
							<mat-icon color="primary">mark_email_read</mat-icon>
							<span>Already sent to customer <strong>{{ emailPreview.customer_invoice_email_sent_at | date:'medium' }}</strong></span>
						</div>
						<div class="sipd-email-info" *ngIf="emailPreview?.invoice_ref_is_estimate">
							<mat-icon>info</mat-icon>
							<span>Invoice <strong>#{{ emailPreview.erp_invoice_transno }}</strong> is the next expected number — actual number assigned at post time (usually the same).</span>
						</div>
						<div class="sipd-email-meta-bar" *ngIf="emailPreview">
							<div class="sipd-em-row">
								<span class="sipd-em-label">To</span>
								<div class="sipd-email-to-block">
									<ng-container *ngIf="emailPreview.recipients?.length">
										<span class="sipd-email-chip" *ngFor="let e of emailPreview.recipients">{{ e }}</span>
									</ng-container>
									<span *ngIf="!emailPreview.recipients?.length" class="sipd-email-none">No address — configure debtor invoice email, INVOICE contacts, or branch email.</span>
								</div>
							</div>
							<p class="sipd-email-source" *ngIf="emailPreview.recipient_source_label">{{ emailPreview.recipient_source_label }}</p>
							<div class="sipd-em-row">
								<span class="sipd-em-label">From</span>
								<span>{{ emailPreview.sender_name }} &lt;{{ emailPreview.from_email }}&gt;</span>
							</div>
							<div class="sipd-em-row">
								<span class="sipd-em-label">Subject</span>
								<span>{{ emailPreview.subject }}</span>
							</div>
							<div class="sipd-em-row">
								<span class="sipd-em-label">Attachment</span>
								<span *ngIf="emailPreview.has_erp_invoice">Invoice-{{ emailPreview.erp_invoice_transno }}.pdf</span>
								<span *ngIf="!emailPreview.has_erp_invoice" class="text-muted">Invoice not posted — PDF only on live send after post</span>
							</div>
						</div>
						<div class="sipd-test-send" *ngIf="emailPreview">
							<mat-form-field appearance="outline" class="sipd-test-addr">
								<mat-label>Email</mat-label>
								<input matInput [(ngModel)]="testEmailAddress" placeholder="you@example.com" type="email">
							</mat-form-field>
							<button mat-stroked-button [disabled]="testEmailBusy || !testEmailAddress" (click)="sendTestEmail()">
								<mat-spinner *ngIf="testEmailBusy" diameter="16"></mat-spinner>
								<mat-icon *ngIf="!testEmailBusy">send</mat-icon>
								{{ testEmailBusy ? 'Sending…' : 'Send' }}
							</button>
							<span class="sipd-test-result sipd-test-ok" *ngIf="testEmailResult?.success">{{ testEmailResult.message }}</span>
							<span class="sipd-test-result sipd-test-err" *ngIf="testEmailResult && !testEmailResult.success">{{ testEmailResult.message }}</span>
						</div>
						<div class="sipd-email-body-wrap" *ngIf="safeEmailHtml">
							<div class="sipd-email-html-host" [innerHTML]="safeEmailHtml"></div>
						</div>
						<p class="sipd-email-fetch-err" *ngIf="emailPreviewError">{{ emailPreviewError }}</p>
						<p class="sipd-email-empty text-muted" *ngIf="!emailPreview && !emailPreviewError">No email preview returned.</p>
					</div>
				</ng-template>
			</mat-tab>

			<mat-tab label="Payments">
				<ng-template matTabContent>
					<div class="sipd-tab-scroll sipd-tab-payments">
						<div class="sipd-preauth-banner" *ngIf="preview.preauth.enabled" [ngClass]="preauthBannerClass()">
							<mat-icon>{{ preauthBannerIcon() }}</mat-icon>
							<div class="sipd-preauth-content">
								<div class="sipd-preauth-title">{{ preauthTitle() }}</div>
								<div class="sipd-preauth-msg" *ngIf="preview.preauth.message && displayStatus !== 'invoiced'">{{ preview.preauth.message }}</div>
								<div class="sipd-preauth-posted" *ngIf="displayStatus === 'invoiced'">
									<span>Invoice is posted in ERP. Details below are for audit (gateway capture may already be complete).</span>
									<span class="sipd-pa-captured" *ngIf="preview.preauth.captured_auth_total > 0">
										<strong>Captured:</strong> {{ preview.preauth.captured_auth_total | currency }}
									</span>
								</div>
								<div class="sipd-preauth-amounts" *ngIf="displayStatus !== 'invoiced'">
									<span class="sipd-pa-item"><strong>Active (uncaptured) hold:</strong> {{ preview.preauth.active_auth_total | currency }}</span>
									<span class="sipd-pa-sep">·</span>
									<span class="sipd-pa-item"><strong>This shipment slice:</strong> {{ preview.preauth.invoice_total | currency }}</span>
									<ng-container *ngIf="preview.preauth.active_auth_total !== preview.preauth.invoice_total">
										<span class="sipd-pa-sep">·</span>
										<span class="sipd-pa-item" [ngClass]="preview.preauth.mismatch ? 'sipd-pa-mismatch' : 'sipd-pa-ok'">
											Δ {{ (preview.preauth.invoice_total - preview.preauth.active_auth_total) | currency }}
										</span>
									</ng-container>
									<span class="sipd-pa-sep" *ngIf="preview.preauth.new_auth_required">·</span>
									<span class="sipd-pa-item sipd-pa-newauth" *ngIf="preview.preauth.new_auth_required">New auth will be captured at invoice</span>
								</div>
								<div class="sipd-preauth-cart" *ngIf="preview.preauth.payment_cart">
									<mat-icon class="sipd-cart-icon">credit_card</mat-icon>
									<span>Payment cart: <strong>{{ preview.preauth.payment_cart.amount | currency }}</strong></span>
									<span class="sipd-cart-hold" *ngIf="preview.preauth.payment_cart.has_card_hold"> · Card hold on file</span>
									<span class="sipd-cart-nohold" *ngIf="!preview.preauth.payment_cart.has_card_hold"> · No card hold</span>
								</div>
								<div class="sipd-preauth-auths" *ngIf="preview.preauth.all_auths && preview.preauth.all_auths.length > 0">
									<div class="sipd-auth-table-title">Card authorizations</div>
									<div class="sipd-auth-cards">
										<div *ngFor="let a of preview.preauth.all_auths" class="sipd-auth-card" [ngClass]="authRowClass(a)">
											<div class="sipd-auth-card-row1">
												<span class="sipd-auth-status-badge" [ngClass]="authStatusClass(a)">{{ a.status_label }}</span>
												<span class="sipd-auth-card-amt">{{ a.amount | currency }}</span>
											</div>
											<div class="sipd-auth-card-row2">{{ a.transdate | date:'medium' }}</div>
											<div class="sipd-auth-card-ref" *ngIf="a.transid" [matTooltip]="a.transid" matTooltipShowDelay="300"><span class="sipd-auth-ref-lbl">Ref</span> {{ authShortRef(a) }}</div>
										</div>
									</div>
								</div>
								<div class="sipd-no-auths" *ngIf="!preauthHasAuthRecords && (!preview.shipment_slice_total || preview.shipment_slice_total <= 0 || preview.preauth.mismatch)">
									<mat-icon>info_outline</mat-icon> No authorization records found for this order.
								</div>
							</div>
						</div>

						<div class="sipd-no-preauth" *ngIf="!preview.preauth.enabled">
							<mat-icon>lock_open</mat-icon>
							<span *ngIf="preview.preauth.globally_configured === true">No card pre-authorization applies to this order (terms or no card workflow).</span>
							<span *ngIf="preview.preauth.globally_configured !== true">Pre-authorization not enabled — no auth capture will occur at invoice</span>
						</div>
					</div>
				</ng-template>
			</mat-tab>
		</mat-tab-group>
	</ng-container>

	<div class="sipd-actions" *ngIf="!loading">
		<ng-container *ngIf="preview">
			<button mat-raised-button color="primary"
				*ngIf="canApprove"
				(click)="closeWith('approve')"
				matTooltip="Approve for invoicing later">
				<mat-icon>check</mat-icon> Approve
			</button>
			<button mat-raised-button color="accent"
				*ngIf="canApproveAndInvoice"
				(click)="closeWith('approve_and_invoice')"
				matTooltip="Approve and invoice now">
				<mat-icon>receipt_long</mat-icon> Approve &amp; Invoice Now
			</button>
			<button mat-raised-button color="primary"
				*ngIf="canRetry"
				(click)="closeWith('retry')"
				matTooltip="Retry invoice">
				<mat-icon>refresh</mat-icon> Retry Invoice
			</button>
			<button mat-stroked-button color="accent"
				*ngIf="canMarkCaptured"
				(click)="closeWith('mark_captured')"
				matTooltip="Record missing ERP capture / card transaction rows (does not call Stripe)">
				<mat-icon>credit_card</mat-icon> Record ERP capture
			</button>
			<button mat-raised-button color="warn"
				*ngIf="canCaptureApplyToInvoice"
				[disabled]="captureApplyBusy"
				(click)="captureApplyToInvoice()"
				matTooltip="Calls Stripe to capture the open hold, posts the card receipt in ERP, and allocates it to this invoice.">
				<mat-icon *ngIf="!captureApplyBusy">payments</mat-icon>
				{{ captureApplyBusy ? 'Capturing…' : 'Capture & apply to invoice' }}
			</button>
		</ng-container>
		<button mat-stroked-button (click)="close()">Close</button>
	</div>
</div>
`,
	styles: [`
:host { display: block; height: 100%; min-height: 0; }
.sipd-wrap { display: flex; flex-direction: column; min-height: 0; width: 100%; height: 100%; max-height: 88vh; overflow: hidden; }
.sipd-tab-group { flex: 1; min-height: 0; width: 100%; }
.sipd-tab-scroll { overflow-x: auto; overflow-y: auto; max-height: calc(92vh - 220px); padding: 8px 12px 20px; box-sizing: border-box; }
.sipd-tab-email .sipd-email-html-host { max-height: min(52vh, 520px); }
.sipd-tab-payments .sipd-preauth-banner { max-height: none; }
.sipd-email-empty { margin: 12px 0; font-size: 0.9rem; }
.sipd-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px 12px; border-bottom: 1px solid #e0e0e0; flex-shrink: 0; }
.sipd-title { display: flex; align-items: center; gap: 8px; font-size: 1.1rem; font-weight: 600; }
.sipd-title-icon { color: #1976d2; }
.sipd-order-no { color: #555; font-weight: 400; }
.sipd-close-btn { margin-left: auto; }
.sipd-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px; gap: 16px; }
.sipd-error { display: flex; align-items: center; gap: 8px; padding: 24px; color: #c62828; }
.sipd-meta-row { display: flex; flex-wrap: wrap; gap: 12px 24px; padding: 14px 20px; background: #f5f5f5; border-bottom: 1px solid #e0e0e0; flex-shrink: 0; }
.sipd-meta-block { display: flex; flex-direction: column; }
.sipd-meta-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: .04em; color: #888; }
.sipd-meta-value { font-size: 0.9rem; font-weight: 500; margin-top: 2px; }
.sipd-status-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 0.78rem; font-weight: 600; text-transform: capitalize; }
.status-pending { background: #fff3e0; color: #e65100; }
.status-approved { background: #e3f2fd; color: #1565c0; }
.status-invoicing { background: #e8f5e9; color: #2e7d32; }
.status-invoiced { background: #e8f5e9; color: #1b5e20; }
.status-failed { background: #ffebee; color: #b71c1c; }
.status-rejected { background: #fce4ec; color: #880e4f; }
.status-on_hold { background: #f3e5f5; color: #6a1b9a; }
.sipd-preauth-banner { display: flex; gap: 12px; padding: 12px 20px; flex-shrink: 1; min-height: 0; max-height: min(380px, 42vh); overflow-x: hidden; overflow-y: auto; }
.sipd-preauth-ok { background: #e8f5e9; border-left: 4px solid #43a047; }
.sipd-preauth-warn { background: #fff8e1; border-left: 4px solid #fb8c00; }
.sipd-preauth-history { background: #e3f2fd; border-left: 4px solid #1976d2; }
.sipd-preauth-banner.sipd-preauth-history mat-icon { color: #1976d2; }
.sipd-preauth-posted { font-size: 0.85rem; color: #444; margin-bottom: 6px; display: flex; flex-wrap: wrap; gap: 8px 16px; align-items: center; }
.sipd-pa-captured { margin-left: 4px; }
.sipd-preauth-banner mat-icon { margin-top: 2px; flex-shrink: 0; }
.sipd-preauth-ok mat-icon { color: #43a047; }
.sipd-preauth-warn mat-icon { color: #fb8c00; }
.sipd-preauth-content { flex: 1; }
.sipd-preauth-title { font-weight: 600; font-size: 0.9rem; margin-bottom: 4px; }
.sipd-preauth-msg { font-size: 0.85rem; color: #555; margin-bottom: 6px; }
.sipd-preauth-amounts { display: flex; flex-wrap: wrap; gap: 4px 12px; font-size: 0.88rem; margin-bottom: 6px; }
.sipd-pa-sep { color: #bbb; }
.sipd-pa-newauth { color: #e65100; font-weight: 600; }
.sipd-preauth-cart { display: flex; align-items: center; gap: 6px; font-size: 0.85rem; margin-bottom: 6px; color: #555; }
.sipd-cart-icon { font-size: 16px; height: 16px; width: 16px; color: #1976d2; }
.sipd-cart-hold { color: #2e7d32; font-weight: 500; }
.sipd-cart-nohold { color: #888; }
.sipd-pa-mismatch { color: #c62828; font-weight: 700; }
.sipd-pa-ok { color: #2e7d32; font-weight: 600; }
.sipd-auth-table-title { font-size: 0.72rem; text-transform: uppercase; letter-spacing: .06em; color: #888; margin: 10px 0 8px; font-weight: 600; }
.sipd-auth-cards { display: flex; flex-direction: column; gap: 8px; }
.sipd-auth-cards--inline { margin-top: 10px; }
.sipd-auth-card {
	border: 1px solid #e8eaed;
	border-radius: 8px;
	padding: 10px 12px;
	background: #fff;
	box-sizing: border-box;
}
.sipd-auth-card.sipd-auth-row-active {
	border-color: #c8e6c9;
	background: linear-gradient(180deg, #f9fff9 0%, #f1f8e9 100%);
	box-shadow: 0 1px 2px rgba(0,0,0,.04);
}
.sipd-auth-card.sipd-auth-row-inactive {
	opacity: 0.78;
	background: #fafafa;
}
.sipd-auth-card-row1 {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	flex-wrap: wrap;
}
.sipd-auth-card-amt { font-size: 0.95rem; font-weight: 700; color: #1a1a1a; white-space: nowrap; }
.sipd-auth-card-row2 { font-size: 0.8rem; color: #666; margin-top: 6px; }
.sipd-auth-card-ref {
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-size: 0.72rem;
	color: #888;
	margin-top: 6px;
	cursor: default;
	word-break: break-all;
	line-height: 1.35;
}
.sipd-auth-ref-lbl { font-family: Arial, Helvetica, sans-serif; color: #9e9e9e; margin-right: 4px; font-size: 0.68rem; text-transform: uppercase; letter-spacing: .04em; }
.sipd-auth-status-badge { display: inline-block; padding: 3px 8px; border-radius: 6px; font-size: 0.75rem; font-weight: 600; white-space: nowrap; max-width: 100%; }
.auth-approved { background: #e3f2fd; color: #1565c0; }
.auth-requires-capture { background: #fff3e0; color: #e65100; }
.auth-captured { background: #e8f5e9; color: #1b5e20; }
.auth-voided { background: #f5f5f5; color: #757575; }
.auth-failed { background: #ffebee; color: #b71c1c; }
.auth-expired { background: #fce4ec; color: #880e4f; }
.auth-other { background: #f5f5f5; color: #555; }
.sipd-no-auths { display: flex; align-items: center; gap: 6px; font-size: 0.82rem; color: #888; margin-top: 6px; }
.sipd-no-preauth { display: flex; align-items: center; gap: 8px; padding: 10px 20px; font-size: 0.85rem; color: #888; background: #fafafa; border-bottom: 1px solid #eee; }
.sipd-section-title { padding: 10px 20px 4px; font-size: 0.78rem; text-transform: uppercase; letter-spacing: .06em; color: #888; font-weight: 600; flex-shrink: 0; }
.sipd-lines-wrap { flex: 1 1 220px; min-height: 0; overflow-x: auto; overflow-y: auto; padding: 0 20px; -webkit-overflow-scrolling: touch; }
.sipd-lines-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
.sipd-lines-table th { background: #f5f5f5; padding: 6px 8px; border-bottom: 2px solid #e0e0e0; white-space: nowrap; font-weight: 600; }
.sipd-lines-table td { padding: 5px 8px; border-bottom: 1px solid #f0f0f0; }
.sipd-lines-table .text-right { text-align: right; }
.sipd-sku { font-family: monospace; font-size: 0.85rem; }
.sipd-line-mismatch td { background: #fff8e1 !important; }
.sipd-line-zero td { color: #bbb; }
.sipd-qty-mismatch { color: #e65100; font-weight: 600; }
.sipd-mismatch-icon { font-size: 14px; height: 14px; width: 14px; vertical-align: middle; color: #fb8c00; margin-left: 2px; }
.sipd-totals-note { padding: 8px 20px; font-size: 0.85rem; color: #555; background: #fafafa; border-top: 1px solid #eee; }
.sipd-totals-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding: 12px 20px; border-top: 1px solid #e0e0e0; flex-shrink: 0; flex-wrap: wrap; }
.sipd-totals-auth { flex: 1 1 260px; min-width: 200px; max-width: 420px; border: 1px solid #e0e0e0; border-radius: 6px; padding: 10px 14px; background: #fafafa; }
.sipd-totals-auth--none { display: flex; align-items: center; gap: 8px; color: #aaa; font-size: 0.85rem; background: transparent; border: none; }
.sipd-totals-auth-title { font-weight: 600; font-size: 0.85rem; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
.sipd-ta-icon { font-size: 18px; height: 18px; width: 18px; }
.sipd-totals-auth-title .sipd-ta-icon.sipd-preauth-ok { color: #2e7d32; }
.sipd-totals-auth-title .sipd-ta-icon.sipd-preauth-warn { color: #ef6c00; }
.sipd-totals-auth-title .sipd-ta-icon.sipd-preauth-history { color: #1565c0; }
.sipd-ta-line { display: flex; justify-content: space-between; font-size: 0.82rem; padding: 2px 0; color: #555; }
.sipd-ta-warn { color: #c62828; font-weight: 600; }
.sipd-ta-info { color: #e65100; font-size: 0.8rem; }
.sipd-ta-noauth { font-size: 0.82rem; }
.sipd-ta-auths { margin-top: 4px; max-width: 100%; }
.sipd-no-auths--inline { font-size: 0.78rem; margin-top: 4px; }
.sipd-totals-block { min-width: 280px; flex-shrink: 0; }
.sipd-totals-line { display: flex; justify-content: space-between; padding: 3px 0; font-size: 0.88rem; }
.sipd-totals-subtotal { border-top: 1px solid #e0e0e0; padding-top: 6px; margin-top: 4px; }
.sipd-totals-tax { color: #555; }
.sipd-totals-total { font-size: 1rem; font-weight: 700; border-top: 2px solid #333; padding-top: 6px; margin-top: 4px; }
.sipd-prev-error { display: flex; align-items: flex-start; gap: 8px; padding: 10px 20px; background: #ffebee; font-size: 0.85rem; color: #b71c1c; flex-shrink: 0; }
.sipd-email-warn, .sipd-email-sent, .sipd-email-info { display: flex; align-items: flex-start; gap: 8px; padding: 8px 20px; font-size: 0.85rem; color: #555; }
.sipd-email-warn mat-icon { color: #f57c00; flex-shrink: 0; }
.sipd-email-info mat-icon { color: #1565c0; flex-shrink: 0; }
.sipd-email-meta-bar { padding: 8px 20px 4px; border-bottom: 1px solid #eee; }
.sipd-em-row { display: flex; flex-wrap: wrap; gap: 6px 12px; align-items: baseline; padding: 4px 0; font-size: 0.88rem; }
.sipd-em-label { min-width: 80px; font-size: 0.72rem; text-transform: uppercase; letter-spacing: .04em; color: #888; flex-shrink: 0; }
.sipd-email-to-block { flex: 1; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.sipd-email-chip { display: inline-block; padding: 4px 10px; background: #e3f2fd; border-radius: 16px; font-size: 0.82rem; font-family: monospace; }
.sipd-email-none { color: #c62828; font-size: 0.85rem; }
.sipd-email-source { margin: 2px 0 0 92px; font-size: 0.78rem; color: #888; font-style: italic; }
.sipd-test-send { display: flex; align-items: center; gap: 12px; padding: 12px 20px; border-bottom: 1px solid #eee; flex-wrap: wrap; }
.sipd-test-addr { flex: 1; min-width: 220px; max-width: 360px; }
.sipd-test-addr .mat-mdc-form-field-subscript-wrapper { display: none; }
.sipd-test-result { font-size: 0.85rem; }
.sipd-test-ok { color: #2e7d32; }
.sipd-test-err { color: #c62828; }
.sipd-email-body-wrap { padding: 12px 20px; }
.sipd-email-html-host { overflow: auto; border: 1px solid #e0e0e0; border-radius: 6px; background: #fff; padding: 8px; }
.sipd-email-fetch-err { margin: 12px 0 0; font-size: 0.82rem; color: #c62828; }
.sipd-actions { display: flex; align-items: center; gap: 10px; padding: 14px 20px; border-top: 1px solid #e0e0e0; flex-shrink: 0; justify-content: flex-end; }
`]
})
export class ShipmentInvoicePreviewDialogComponent implements OnInit {
	loading = true;
	error: string | null = null;
	preview: any = null;
	emailPreview: any = null;
	emailPreviewError: string | null = null;
	captureApplyBusy = false;
	testEmailAddress = '';
	testEmailBusy = false;
	testEmailResult: { success: boolean; message: string } | null = null;

	constructor(
		private dialogRef: MatDialogRef<ShipmentInvoicePreviewDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { shipment: Shipment; tpl3plService: Tpl3plService },
		private globalSearchService: GlobalSearchService,
		private sanitizer: DomSanitizer
	) {}

	get safeEmailHtml(): SafeHtml | null {
		const h = this.emailPreview?.html_body;
		if (!h || typeof h !== 'string') {
			return null;
		}
		return this.sanitizer.bypassSecurityTrustHtml(h);
	}

	ngOnInit(): void {
		const sid = this.data.shipment.id;
		forkJoin({
			invoice: this.data.tpl3plService.getInvoicePreview(sid).pipe(
				catchError((err) =>
					of({
						success: false,
						message: err?.error?.message || err?.message || 'Could not load invoice preview',
						preview: null
					})
				)
			),
			email: this.data.tpl3plService.getCustomerInvoiceEmailPreview(sid).pipe(
				catchError((err) =>
					of({
						success: false,
						message: err?.error?.message || err?.message || 'Could not load email preview',
						preview: null
					})
				)
			)
		}).subscribe({
			next: ({ invoice, email }) => {
				this.loading = false;
				if (invoice.success && invoice.preview) {
					this.preview = invoice.preview;
					if (!Array.isArray(this.preview.lines)) {
						this.preview.lines = [];
					}
				} else {
					this.error = invoice.message || 'Could not load preview';
				}
				if (email?.success && email.preview) {
					this.emailPreview = email.preview;
				} else if (email && !email.success) {
					this.emailPreviewError = email.message || 'Email preview unavailable';
				}
			},
			error: () => {
				this.loading = false;
				this.error = 'Error loading preview';
			}
		});
	}

	/** Matches list resolveInvoiceStatus (phase + ERP invoice). */
	get displayStatus(): string {
		const sh = this.preview?.shipment;
		const eff = sh?.effective_invoice_status;
		if (eff && String(eff).trim() !== '') {
			return String(eff).trim().toLowerCase();
		}
		return String(sh?.invoice_status || 'pending').trim().toLowerCase() || 'pending';
	}

	get displayStatusLabel(): string {
		const labels: Record<string, string> = {
			pending: 'Pending',
			approved: 'Approved',
			invoicing: 'Invoicing',
			invoiced: 'Invoiced',
			failed: 'Failed',
			rejected: 'Rejected',
			on_hold: 'On hold'
		};
		return labels[this.displayStatus] || this.displayStatus;
	}

	/** Backend sends has_auth_records; older payloads infer from all_auths. */
	get preauthHasAuthRecords(): boolean {
		const p = this.preview?.preauth;
		if (!p) {
			return false;
		}
		if (p.has_auth_records !== undefined) {
			return !!p.has_auth_records;
		}
		return Array.isArray(p.all_auths) && p.all_auths.length > 0;
	}

	preauthBannerClass(): string {
		if (this.displayStatus === 'invoiced' && this.preview?.preauth?.enabled) {
			return 'sipd-preauth-history';
		}
		const p = this.preview?.preauth;
		if (!p) {
			return 'sipd-preauth-ok';
		}
		if (p.mismatch) {
			return 'sipd-preauth-warn';
		}
		if (!this.preauthHasAuthRecords && (this.preview?.shipment_slice_total ?? 0) > 0.004) {
			return 'sipd-preauth-warn';
		}
		return 'sipd-preauth-ok';
	}

	preauthBannerIcon(): string {
		if (this.displayStatus === 'invoiced') {
			return 'check_circle';
		}
		const p = this.preview?.preauth;
		if (p?.mismatch) {
			return 'warning';
		}
		if (!this.preauthHasAuthRecords && (this.preview?.shipment_slice_total ?? 0) > 0.004) {
			return 'credit_card';
		}
		return 'lock';
	}

	preauthTitle(): string {
		if (this.displayStatus === 'invoiced' && this.preview?.preauth?.enabled) {
			return 'Payment / authorization history';
		}
		const p = this.preview?.preauth;
		if (!p) {
			return '';
		}
		if (p.mismatch) {
			return 'Pre-auth mismatch — action required before invoicing';
		}
		if (!this.preauthHasAuthRecords && (this.preview?.shipment_slice_total ?? 0) > 0.004) {
			return 'Card terms — no matching authorization on file';
		}
		if (this.preauthHasAuthRecords) {
			return 'Pre-authorization on file';
		}
		return 'Authorization';
	}

	get canMarkCaptured(): boolean {
		const s = this.data?.shipment;
		if (!s) {
			return false;
		}
		return !!(s.reconcile_auth_id || s.reconcile_transid);
	}

	/** Posted invoice in ERP but card hold still open — capture via gateway and allocate payment. */
	get canCaptureApplyToInvoice(): boolean {
		if (this.displayStatus !== 'invoiced' || !this.preview?.preauth?.enabled) {
			return false;
		}
		const active = this.preview.preauth.active_auth_total;
		if (active == null || active <= 0.004) {
			return false;
		}
		const poa = this.preview.preauth.primary_open_auth;
		if (!poa || (!poa.transid && !poa.auth_id)) {
			return false;
		}
		return !!(this.preview.shipment?.invoice_transno || this.preview.shipment?.erp_invoice_id);
	}

	get canApprove(): boolean {
		const s = this.displayStatus;
		return s === 'pending' || s === 'on_hold';
	}

	get canApproveAndInvoice(): boolean {
		const s = this.displayStatus;
		return s === 'pending' || s === 'approved' || s === 'on_hold';
	}

	get canRetry(): boolean {
		return this.displayStatus === 'failed';
	}

	authStatusClass(auth: any): string {
		const s = (auth?.status || '').toLowerCase().trim();
		if (s === '1' || s === 'approved') return 'auth-approved';
		if (s === 'requires_capture') return 'auth-requires-capture';
		if (s === 'c' || s === 'captured') return 'auth-captured';
		if (s === 'voided' || s === 'v') return 'auth-voided';
		if (s === 'declined' || s === 'failed') return 'auth-failed';
		if (s === 'expired') return 'auth-expired';
		return 'auth-other';
	}

	/** Short gateway reference for display; full ID in tooltip. */
	authShortRef(auth: any): string {
		const id = String(auth?.transid ?? '').trim();
		if (!id) {
			return '';
		}
		if (id.length <= 14) {
			return id;
		}
		return '…' + id.slice(-10);
	}

	authRowClass(auth: any): string {
		return auth?.is_active ? 'sipd-auth-row-active' : 'sipd-auth-row-inactive';
	}

	sendTestEmail(): void {
		const addr = (this.testEmailAddress || '').trim();
		if (!addr) return;
		const shipmentId = this.data?.shipment?.id;
		if (!shipmentId) return;
		this.testEmailBusy = true;
		this.testEmailResult = null;
		this.data.tpl3plService.sendTestCustomerInvoiceEmail(shipmentId, addr).subscribe({
			next: (res) => {
				this.testEmailBusy = false;
				this.testEmailResult = res;
			},
			error: () => {
				this.testEmailBusy = false;
				this.testEmailResult = { success: false, message: 'Request failed' };
			}
		});
	}

	close(): void {
		this.dialogRef.close();
	}

	closeWith(action: string): void {
		this.dialogRef.close(action);
	}

	captureApplyToInvoice(): void {
		const orderno = this.preview?.order?.orderno || this.data.shipment?.orderno;
		const poa = this.preview?.preauth?.primary_open_auth;
		const invNo = this.preview?.shipment?.invoice_transno;
		if (!orderno || !poa) {
			this.globalSearchService.showNotification('Missing order or open authorization for capture', 'warning', 'bottom', 'right');
			return;
		}
		const suggested = this.preview?.totals?.total;
		const msg =
			`Capture the card hold in Stripe and post payment to invoice${invNo != null ? ' #' + invNo : ''}?\n\n`
			+ `Suggested amount: $${typeof suggested === 'number' ? suggested.toFixed(2) : '—'} (server will cap to auth and open balance).`;
		if (!window.confirm(msg)) {
			return;
		}
		this.captureApplyBusy = true;
		this.data.tpl3plService
			.captureAuthApplyInvoice({
				orderno,
				transid: poa.transid || '',
				auth_id: poa.auth_id || '',
				invoice_transno: invNo != null ? Number(invNo) : undefined
			})
			.subscribe({
				next: (res) => {
					this.captureApplyBusy = false;
					if (res?.success) {
						this.globalSearchService.showNotification(res.message || 'Captured and applied to invoice', 'success', 'bottom', 'right');
						this.dialogRef.close('capture_applied');
					} else {
						this.globalSearchService.showNotification(res?.message || 'Capture and apply failed', 'warning', 'bottom', 'right');
					}
				},
				error: (err) => {
					this.captureApplyBusy = false;
					this.globalSearchService.showNotification(err?.error?.message || 'Capture and apply failed', 'danger', 'bottom', 'right');
				}
			});
	}
}
